import { makeTrpcClient, trpc, useSession } from '@tg/app-core';
import { QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query';
import { persistQueryClientRestore, persistQueryClientSubscribe, type PersistedClient, type Persister } from '@tanstack/react-query-persist-client';
import { useEffect, useState, type ReactNode } from 'react';
import { pathOf } from './days';
import { idbDel, idbGet, idbSet, openDb } from './idb';
import { offlineLink } from './link';
import { initOutbox, resetOutbox } from './outbox';
import { OfflineBar } from './Status';
import { initSync, stopSync } from './sync';

/* Данные кабинета тренера с работой без сети. Вместо общего ApiProvider: тот же React Query и tRPC,
   плюс кэш ответов в IndexedDB (cache), очередь правок (outbox) и плашка состояния связи.
   Пока связь есть, кабинет работает как раньше: запросы идут на сервер, свежие данные — с сервера. */

const WEEK = 7 * 864e5;
/** Поменялся формат данных на устройстве — кэш сбрасывается (очередь правок остаётся) */
const BUSTER = 'offline-1';

let writeTimer: ReturnType<typeof setTimeout> | undefined;
let latest: PersistedClient | null = null;
const persister: Persister = {
  /* Кэш пишется не чаще раза в секунду — React Query сообщает о каждом изменении */
  persistClient: (c) => {
    latest = c;
    if (!writeTimer)
      writeTimer = setTimeout(() => {
        writeTimer = undefined;
        if (latest) idbSet('cache', 'client', latest);
      }, 1000);
  },
  restoreClient: () => idbGet<PersistedClient>('cache', 'client'),
  removeClient: () => idbDel('cache', 'client').then(() => undefined),
};

/** На устройство — ответы кабинета и базы упражнений (видео и ИИ — нет) */
const keep = (q: Query) => {
  if (q.state.status !== 'success') return false;
  const p = pathOf(q.queryKey);
  return (p.startsWith('trainer.') || p.startsWith('catalog.')) && p !== 'trainer.videoStatus';
};

export function TrainerData({ children }: { children: ReactNode }) {
  const [qc] = useState(
    () =>
      new QueryClient({
        /* networkMode 'always': без сети запрос не «засыпает», а получает ответ с устройства (offlineLink) */
        defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false, networkMode: 'always' }, mutations: { networkMode: 'always' } },
      }),
  );
  const [client] = useState(() => makeTrpcClient([offlineLink(qc)]));
  const s = useSession();
  const pid = s?.role === 'trainer' ? s.pid : null;
  /* Первый показ — после чтения кэша с устройства (доли секунды), чтобы без сети сразу были данные */
  const [ready, setReady] = useState(() => !pid);

  useEffect(() => {
    if (!pid) {
      openDb(null);
      resetOutbox();
      stopSync();
      setReady(true);
      return;
    }
    let alive = true;
    let unsub = () => {};
    openDb(pid);
    (async () => {
      try {
        await persistQueryClientRestore({ queryClient: qc, persister, maxAge: WEEK, buster: BUSTER });
      } catch {
        /* кэш не прочитался — живём без него */
      }
      if (!alive) return;
      unsub = persistQueryClientSubscribe({ queryClient: qc, persister, buster: BUSTER, dehydrateOptions: { shouldDehydrateQuery: keep } });
      await initOutbox(qc);
      await initSync(qc);
      if (alive) setReady(true);
    })();
    return () => {
      alive = false;
      unsub();
    };
  }, [pid]);

  if (!ready) return null;
  return (
    <trpc.Provider client={client} queryClient={qc}>
      <QueryClientProvider client={qc}>
        {children}
        {pid ? <OfflineBar /> : null}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
